import { type useIntl } from 'react-intl'

import { type SupportLine } from '@onr/schema'
import dayjs from 'dayjs'

import { initialsOf } from '@/components/atoms/Avatar'
import { type SupportAgentQueueRow } from '@/components/organisms/SupportAgentQueue'
import { type SupportConversationItem } from '@/components/organisms/SupportConversation'
import { type SupportRequestAgentDocument, type SupportRequestItemDocument } from '@/state/support/types'

import { type useSupportLabels } from './useSupportLabels'

type Intl = ReturnType<typeof useIntl>
interface Person {
  firstName: string
  lastName: string
}

const linkedEventMessage = (fromDiagnostic: boolean, replacing: boolean) => {
  if (fromDiagnostic) {
    return replacing ? 'soutien.agentEvent.linked.fromDiagnosticReplacing' : 'soutien.agentEvent.linked.fromDiagnostic'
  }
  return replacing ? 'soutien.agentEvent.linked.replacing' : 'soutien.agentEvent.linked'
}

export const fullName = ({ firstName, lastName }: Person) => `${firstName} ${lastName}`

const durationSince = (intl: Intl, since: Date, now: Date) => {
  const minutes = Math.max(1, dayjs(now).diff(since, 'minute'))
  if (minutes < 60) {
    return intl.formatMessage({ id: 'soutien.duration.minutes' }, { count: minutes })
  }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return intl.formatMessage({ id: 'soutien.duration.hours' }, { count: hours })
  }
  return intl.formatMessage({ id: 'soutien.duration.days' }, { count: Math.floor(hours / 24) })
}

export const queueRow = (
  {
    number,
    centralName,
    category,
    statusSince,
    title,
    blocking,
    authorName,
    status,
    assignee,
  }: SupportRequestAgentDocument,
  {
    line,
    me,
    unread,
    intl,
    labels,
    now,
  }: {
    line: SupportLine
    me: number | null
    unread: boolean
    intl: Intl
    labels: ReturnType<typeof useSupportLabels>
    now: Date
  }
): SupportAgentQueueRow => {
  const self = assignee !== null && assignee.piloteUserId === me
  const escouade = line === 'l1'
  return {
    number,
    meta: `#${number} · ${centralName} · ${labels.category(category)}`,
    since: intl.formatMessage({ id: 'soutien.agent.queue.since' }, { duration: durationSince(intl, statusSince, now) }),
    title,
    blocking,
    author: escouade ? authorName : undefined,
    waitingFor:
      escouade && (status === 'waitingRequester' || status === 'waitingValian')
        ? intl.formatMessage({ id: `soutien.status.${status}` })
        : undefined,
    assignee: assignee
      ? {
          tone: self ? 'self' : 'other',
          initials: initialsOf(assignee),
          label: intl.formatMessage(
            { id: self ? 'soutien.avatar.self' : 'soutien.avatar.other' },
            { name: fullName(assignee) }
          ),
        }
      : { tone: 'empty', label: intl.formatMessage({ id: 'soutien.avatar.unassigned' }) },
    unread,
  }
}

export const agentThreadItem = (
  item: SupportRequestItemDocument,
  { line, uid, readUpTo, intl }: { line: SupportLine; uid: string; readUpTo: Date | null; intl: Intl }
): SupportConversationItem | null => {
  const time = intl.formatTime(item.createdAt)
  const isMine = 'author' in item && item.author.uid === uid
  const isNew = !isMine && (!readUpTo || item.createdAt > readUpTo)
  const authorName = (person: Person & { uid: string }) =>
    person.uid === uid ? intl.formatMessage({ id: 'soutien.conversation.self' }) : fullName(person)

  switch (item.kind) {
    case 'message':
      return {
        id: item._id,
        kind: 'message',
        time,
        author: authorName(item.author),
        detail:
          item.from === 'escouade'
            ? intl.formatMessage(
                { id: 'soutien.message.signed' },
                {
                  signature: intl.formatMessage(
                    { id: 'soutien.escouadeSignature' },
                    { firstName: item.author.firstName }
                  ),
                }
              )
            : item.opening
              ? intl.formatMessage({ id: 'soutien.conversation.opening' })
              : undefined,
        newLabel: isNew ? intl.formatMessage({ id: 'soutien.conversation.new' }) : undefined,
        text: item.text,
      }
    case 'note': {
      const author = authorName(item.author)
      const details = [
        ...(line === 'l1' ? [intl.formatMessage({ id: 'soutien.note.hidden' })] : []),
        ...(item.line === 'l2' && item.statusKept ? [intl.formatMessage({ id: 'soutien.note.statusKept' })] : []),
      ]
      return {
        id: item._id,
        kind: 'note',
        time,
        title: intl.formatMessage(
          { id: 'soutien.note.title' },
          {
            author:
              item.line === 'l1' && line === 'l1'
                ? author
                : intl.formatMessage({ id: `soutien.note.author.${item.line}` }, { author }),
          }
        ),
        detail: details.length > 0 ? details.join(' · ') : undefined,
        text: item.text,
      }
    }
    case 'event': {
      if (item.audience !== 'agents') {
        return null
      }
      const actor = item.actor.firstName
      const text = (() => {
        switch (item.type) {
          case 'taken':
            return intl.formatMessage({ id: 'soutien.agentEvent.taken' }, { actor })
          case 'assigned':
            return intl.formatMessage(
              { id: 'soutien.agentEvent.assigned' },
              { actor, assignee: item.assignee?.firstName ?? '' }
            )
          case 'unassigned':
            return intl.formatMessage({ id: 'soutien.agentEvent.unassigned' }, { actor })
          case 'status':
            return intl.formatMessage(
              { id: 'soutien.agentEvent.status' },
              {
                from: item.from ? intl.formatMessage({ id: `soutien.status.${item.from}` }) : '',
                to: item.to ? intl.formatMessage({ id: `soutien.status.${item.to}` }) : '',
              }
            )
          case 'linked':
            return intl.formatMessage(
              { id: linkedEventMessage(Boolean(item.diagnosticAt), Boolean(item.replaces)) },
              {
                actor,
                ticket: item.ticket?.identifier ?? '',
                time: item.diagnosticAt ? intl.formatTime(item.diagnosticAt) : '',
                replaced: item.replaces ?? '',
              }
            )
        }
      })()
      if (item.type === 'linked') {
        return {
          id: item._id,
          kind: 'event',
          time,
          icon: 'link',
          text,
          link: item.ticket ? { label: item.ticket.identifier, href: item.ticket.url } : undefined,
        }
      }
      return { id: item._id, kind: 'event', time, icon: item.type === 'status' ? 'status' : 'taken', text }
    }
    case 'diagnostic': {
      const { firstName } = item.author
      return {
        id: item._id,
        kind: 'diagnostic',
        time,
        title: intl.formatMessage({ id: 'soutien.diagnostic.title' }, { firstName }),
        cause: item.probableCause,
        dateLine: intl.formatMessage(
          { id: 'soutien.diagnostic.dateLine' },
          {
            date: intl.formatDate(item.producedAt, { day: 'numeric', month: 'long', year: 'numeric' }),
            producedAt: intl.formatTime(item.producedAt),
            firstName,
            attachedAt: time,
          }
        ),
        markdown: item.markdown,
      }
    }
  }
}
